import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, DatePicker, InputNumber, message, Button } from 'antd';
import type { Participant, Club, Category } from '@/types/models';
import { apiService } from '@/services/api.service';
import { API_ENDPOINTS } from '@/constants/api.constants';
import { logger } from '@/utils/logger';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import { getDateValueFromEvent, datePickerProps } from '@/utils/datePickerUtils';

dayjs.locale('ru');

interface CreateParticipantModalProps {
  visible: boolean;
  participant?: Participant | null;
  onCancel: () => void;
  onSuccess: () => void;
}

// Новые уровни пояса (кю и дан)
const BELT_LEVELS = [
  '11 кю', // 11 кю
  '10 кю', // 10 кю
  '9 кю',  // 9 кю
  '8 кю',  // 8 кю
  '7 кю',  // 7 кю
  '6 кю',  // 6 кю
  '5 кю',  // 5 кю
  '4 кю',  // 4 кю
  '3 кю',  // 3 кю
  '2 кю',  // 2 кю
  '1 кю',  // 1 кю
  '1 дан', // 1 дан
  '2 дан', // 2 дан
  '3 дан', // 3 дан
  '4 дан', // 4 дан
  '5 дан', // 5 дан
  '6 дан', // 6 дан
  '7 дан', // 7 дан
  '8 дан', // 8 дан
  '9 дан', // 9 дан
  '10 дан' // 10 дан
];

const CreateParticipantModal: React.FC<CreateParticipantModalProps> = ({
  visible,
  participant,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [clubs, setClubs] = useState<Club[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);
  const [coaches, setCoaches] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const isEditing = !!participant;

  // Функция для фильтрации категорий по возрасту, весу и полу
  const filterCategories = (birthDate: dayjs.Dayjs | null, weight: number | null, gender: string | null) => {
    if (!birthDate || !weight || !gender) {
      setFilteredCategories(allCategories);
      return;
    }

    // Рассчитываем возраст
    const today = dayjs();
    let calculatedAge = today.year() - birthDate.year();
    const monthDiff = today.month() - birthDate.month();
    if (monthDiff < 0 || (monthDiff === 0 && today.date() < birthDate.date())) {
      calculatedAge--;
    }

    // Фильтруем категории
    const filtered = allCategories.filter(category => {
      // Проверка возраста
      const ageMatch = calculatedAge >= category.ageFrom && calculatedAge <= category.ageTo;
      
      // Проверка веса
      const weightMatch = weight >= category.weightFrom && weight <= category.weightTo;
      
      // Проверка пола
      const genderMatch = category.gender === gender || category.gender === 'mixed';
      
      return ageMatch && weightMatch && genderMatch;
    });

    setFilteredCategories(filtered);    
  };

  // Загрузка списка клубов
  const fetchClubs = async () => {
    try {
      setLoading(true);
      const { data } = await apiService.get<{ data: Club[], status: number }>(API_ENDPOINTS.CLUBS.BASE);
      if (data?.data) {
        setClubs(data.data);
      }
    } catch (error) {
      logger.error('Error fetching clubs:', error);
      message.error('Ошибка при загрузке списка клубов');
    } finally {
      setLoading(false);
    }
  };

  // Загрузка списка категорий
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const { data } = await apiService.get<{ data: Category[], status: number }>(API_ENDPOINTS.CATEGORIES.BASE);
      if (data?.data) {
        setAllCategories(data.data);
        setFilteredCategories(data.data);
      }
    } catch (error) {
      logger.error('Error fetching categories:', error);
      message.error('Ошибка при загрузке списка категорий');
    } finally {
      setLoading(false);
    }
  };

  // Загрузка списка тренеров
  const fetchCoaches = async () => {
    try {
      setLoading(true);
      const { data } = await apiService.get<{ data: any[], status: number }>(API_ENDPOINTS.COACHES.BASE);
      if (data?.data) {
        setCoaches(data.data);
      }
    } catch (error) {
      logger.error('Error fetching coaches:', error);
      message.error('Ошибка при загрузке списка тренеров');
    } finally {
      setLoading(false);
    }
  };

  // Загружаем список клубов и категорий при открытии модального окна
  useEffect(() => {
    if (visible) {
      fetchClubs();
      fetchCategories();
      fetchCoaches();
      
      // Небольшая задержка для корректной инициализации формы
      setTimeout(() => {
        if (participant) {
          // Редактирование существующего участника
          form.setFieldsValue({
            firstName: participant.firstName,
            lastName: participant.lastName,
            middleName: participant.middleName,
            email: (participant as any).email,
            phone: participant.phone || (participant as any).phoneNumber,
            birthDate: participant.birthDate ? (dayjs(participant.birthDate).isValid() ? dayjs(participant.birthDate) : undefined) : undefined,
            gender: participant.gender || 'male',
            weight: participant.weight,
            beltLevel: participant.beltLevel,
            clubId: participant.clubId,
            categoryId: participant.categoryId,
            coachId: participant.coachId,
            status: participant.status || 'active',
          });
        } else {
          // Создание нового участника - сбрасываем форму и устанавливаем значения по умолчанию
          form.resetFields();
          form.setFieldsValue({
            gender: 'male',
            status: 'active'
          });
        }
      }, 100);
    } else {
      // При закрытии модального окна сбрасываем форму
      form.resetFields();
    }
  }, [visible, participant, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Преобразуем дату рождения в формат YYYY-MM-DD
      let birthDate: string | undefined = undefined;
      if (values.birthDate) {
        if (dayjs.isDayjs(values.birthDate) && values.birthDate.isValid()) {
          birthDate = values.birthDate.format('YYYY-MM-DD');
        } else {
          const parsedDate = dayjs(values.birthDate);
          if (parsedDate.isValid()) {
            birthDate = parsedDate.format('YYYY-MM-DD');
          }
        }
      }

      // Рассчитываем возраст на основе даты рождения (точный расчет с учетом месяца и дня)
      let age: number | undefined = undefined;
      if (values.birthDate) {
        const birthDateDayjs = dayjs.isDayjs(values.birthDate) && values.birthDate.isValid()
          ? values.birthDate
          : dayjs(values.birthDate).isValid()
            ? dayjs(values.birthDate)
            : null;
        if (birthDateDayjs) {
          const today = dayjs();
          let calculatedAge = today.year() - birthDateDayjs.year();
          const monthDiff = today.month() - birthDateDayjs.month();
          if (monthDiff < 0 || (monthDiff === 0 && today.date() < birthDateDayjs.date())) {
            calculatedAge--;
          }
          age = calculatedAge;
        }
      }

      const formData: any = {
        ...values,
        birthDate,
        age,
        belt: values.beltLevel, // Добавляем поле belt, которое требует бэкенд
        weight: parseFloat(values.weight), // Убедимся, что вес передается как число
        phoneNumber: values.phone || values.phoneNumber // Преобразуем phone в phoneNumber для бэкенда
      };
      
      // Удаляем поле phone, так как мы используем phoneNumber
      delete formData.phone;

      if (isEditing && participant) {
        // Обновление существующего участника
        await apiService.put(`${API_ENDPOINTS.PARTICIPANTS.BASE}/${participant.id}`, formData);
        message.success('Участник успешно обновлен');
      } else {
        // Создание нового участника
        await apiService.post(API_ENDPOINTS.PARTICIPANTS.BASE, formData);
        message.success('Участник успешно создан');
      }

      form.resetFields();
      onSuccess();
    } catch (error) {
      logger.error('Error submitting participant form:', error);

      // Улучшенная обработка ошибок
      const err = error as any;
      if (err.response?.data?.errors) {
        const errorMessages = err.response.data.errors
          .map((e: any) => `${e.msg} (${e.path})`)
          .join(', ');
        message.error(`Ошибка: ${errorMessages}`);
      } else {
        message.error('Ошибка при сохранении данных участника');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={isEditing ? 'Редактировать участника' : 'Добавить участника'}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
      style={{ top: 20 }}
      styles={{ body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' } }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ gender: 'male', status: 'active' }}
      >
        <Form.Item
          name="firstName"
          label="Имя"
          rules={[{ required: true, message: 'Введите имя' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="lastName"
          label="Фамилия"
          rules={[{ required: true, message: 'Введите фамилию' }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="middleName"
          label="Отчество"
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="gender"
          label="Пол"
          rules={[{ required: true, message: 'Выберите пол' }]}
        >
          <Select
            onChange={(value) => {
              const birthDate = form.getFieldValue('birthDate');
              const weight = form.getFieldValue('weight');
              filterCategories(birthDate, weight, value);
            }}
          >
            <Select.Option value="male">Мужской</Select.Option>
            <Select.Option value="female">Женский</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="birthDate"
          label="Дата рождения"
          rules={[{ required: true, message: 'Укажите дату рождения' }]}
          getValueFromEvent={getDateValueFromEvent}
        >
          <DatePicker 
            {...datePickerProps}
            placeholder="Выберите дату рождения (ДД.ММ.ГГГГ)"
            onChange={(date) => {
              const gender = form.getFieldValue('gender');
              const weight = form.getFieldValue('weight');
              filterCategories(date, weight, gender);
            }}
          />
        </Form.Item>

        <Form.Item
          name="weight"
          label="Вес (кг)"
          rules={[
            { required: true, message: 'Введите вес' },
            { type: 'number', min: 0.1, message: 'Вес должен быть положительным числом' }
          ]}
        >
          <InputNumber 
            min={0.1} 
            max={200} 
            step={0.1} 
            precision={1}
            style={{ width: '100%' }}
            onChange={(value) => {
              const birthDate = form.getFieldValue('birthDate');
              const gender = form.getFieldValue('gender');
              filterCategories(birthDate, value as number, gender);
            }}
          />
        </Form.Item>

        <Form.Item
          name="beltLevel"
          label="Уровень"
          rules={[{ required: true, message: 'Выберите уровень' }]}
        >
          <Select>
            {BELT_LEVELS.map(belt => (
              <Select.Option key={belt} value={belt}>
                {belt}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="email"
          label="Email"
          rules={[
            { type: 'email', message: 'Введите корректный email' }
          ]}
        >
          <Input type="email" />
        </Form.Item>

        <Form.Item
          name="phone"
          label="Телефон"
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="clubId"
          label="Клуб"
          rules={[{ required: true, message: 'Выберите клуб' }]}
        >
          <Select
            placeholder="Выберите клуб"
            loading={loading}
          >
            {clubs.map(club => (
              <Select.Option key={club.id} value={club.id}>{club.name}</Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="categoryId"
          label="Категория"
          rules={[{ required: true, message: 'Выберите категорию' }]}
          extra={filteredCategories.length === 0 && (form.getFieldValue('birthDate') || form.getFieldValue('weight') || form.getFieldValue('gender')) 
            ? 'Нет подходящих категорий для указанных параметров' 
            : filteredCategories.length < allCategories.length 
              ? `Показано ${filteredCategories.length} из ${allCategories.length} категорий (отфильтровано по возрасту, весу и полу)`
              : undefined}
        >
          <Select
            placeholder="Выберите категорию"
            loading={loading}
            allowClear
          >
            {filteredCategories.map(category => (
              <Select.Option key={category.id} value={category.id}>
                {category.name} ({category.ageFrom}-{category.ageTo} лет, {category.weightFrom}-{category.weightTo} кг, {category.gender === 'male' ? 'М' : category.gender === 'female' ? 'Ж' : 'М/Ж'})
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="coachId"
          label="Тренер"
          rules={[{ required: false, message: 'Выберите тренера' }]}
        >
          <Select
            placeholder="Выберите тренера"
            loading={loading}
            allowClear
          >
            {coaches.map(coach => (
              <Select.Option key={coach.id} value={coach.id}>
                {coach.firstName} {coach.lastName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="status"
          label="Статус"
          rules={[{ required: true, message: 'Выберите статус' }]}
        >
          <Select>
            <Select.Option value="active">Активен</Select.Option>
            <Select.Option value="inactive">Неактивен</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item className="mb-0 text-right">
          <Button onClick={onCancel} style={{ marginRight: 8 }}>
            Отмена
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            {isEditing ? 'Сохранить' : 'Создать'}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateParticipantModal;
